import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft,
  Plus,
  Trash2,
  Settings,
  Database,
  Filter,
  Link
} from 'lucide-react';

// Import the RefConfig interface from the templates file
import { RefConfig } from './ReferenceFieldTemplates';
import { supabase } from '@/lib/supabase';

interface RefFilterGroup {
  col_name: string;
  operator: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'LIKE' | 'ILIKE' | 'IN' | 'IS NULL' | 'IS NOT NULL';
  value: string | number | boolean | null;
}

interface IntermediateTableConfig {
  table: string;
  intermediate_column: string;
  target_column: string;
}

interface CustomReferenceFieldConfigProps {
  initialConfig?: RefConfig;
  onSave: (config: RefConfig) => void;
  onBack: () => void;
}

export function CustomReferenceFieldConfig({ 
  initialConfig, 
  onSave, 
  onBack 
}: CustomReferenceFieldConfigProps) {
  const [config, setConfig] = useState<RefConfig>(initialConfig || {
    ref_table: '',
    ref_col: '',
    ref_lookup_col: 'id',
    field_type: 'TEXT',
    is_editable: true,
    intermediate_tables: []
  });
  
  const [showFilterConfig, setShowFilterConfig] = useState(false);
  const [filterGroup, setFilterGroup] = useState<RefFilterGroup>({
    col_name: '',
    operator: '=',
    value: ''
  });
  
  const [showIntermediateTables, setShowIntermediateTables] = useState(false);
  const [checklistItems, setChecklistItems] = useState<Array<{ id: number; name: string; template: string;}>>([]);
  const isChecklistItemReference = config.ref_table === 'project_checklist_items' && config.ref_col === 'value' && config.ref_lookup_col === 'project_id';

  useEffect(() => {
    if (!isChecklistItemReference) return;
    let isMounted = true;
    (async () => {
      // Fetch all checklist items with their template name for a helpful label
      const { data, error } = await supabase
        .from('checklist_items')
        .select('id, item_name, template_id, templates(name)')
        .order('template_id')
        .order('item_order');
      if (!isMounted) return;
      if (error) {
        console.error('Error fetching checklist items for reference:', error);
        setChecklistItems([]);
        return;
      }
      const items = (data || []).map((row: any) => ({
        id: row.id,
        name: row.item_name,
        template: row.templates?.name || ''
      }));
      setChecklistItems(items);
    })();
    return () => { isMounted = false; };
  }, [isChecklistItemReference]);
  
  // Common table options
  const tableOptions = [
    { label: "Documents", value: "documents" },
    { label: "Contacts", value: "contacts" },
    { label: "Tasks", value: "tasks" },
    { label: "Messages", value: "messages" },
    { label: "Projects", value: "projects" },
    { label: "Teams", value: "teams" },
    { label: "Users", value: "users" },
    { label: "Installers", value: "installer_partners" },
    { label: "Activities", value: "activity_logs" },
    { label: "Notes", value: "notes" },
    { label: "Project Checklist Items", value: "project_checklist_items" }
  ];
  
  const fieldTypeOptions = [
    { label: "Text", value: "TEXT" },
    { label: "Number", value: "NUMBER" },
    { label: "Date", value: "DATE" },
    { label: "Checkbox", value: "CHECKBOX" },
    { label: "Radio Button", value: "RADIOBUTTON" },
    { label: "User Assignment", value: "USER_ASSIGNMENT" },
    { label: "File Upload", value: "FILE_UPLOAD" },
    { label: "Multi Select (read-only)", value: "MULTI_SELECT" }
  ];
  
  const operatorOptions = [
    { label: "Equals", value: "=" },
    { label: "Not Equals", value: "!=" },
    { label: "Greater Than", value: ">" },
    { label: "Less Than", value: "<" },
    { label: "Greater Than or Equal", value: ">=" },
    { label: "Less Than or Equal", value: "<=" },
    { label: "Like", value: "LIKE" },
    { label: "Case Insensitive Like", value: "ILIKE" },
    { label: "In", value: "IN" },
    { label: "Is Null", value: "IS NULL" },
    { label: "Is Not Null", value: "IS NOT NULL" }
  ];
  
  const handleSave = () => {
    // Validate required fields
    if (!config.ref_table || !config.ref_col) {
      alert('Reference table and column are required');
      return;
    }
    
    // Add filter group if configured
    const finalConfig = {
      ...config,
      ref_filter_group: showFilterConfig && filterGroup.col_name ? filterGroup : undefined
    };
    
    onSave(finalConfig);
  };
  
  const addIntermediateTable = () => {
    setConfig(prev => ({
      ...prev,
      intermediate_tables: [
        ...(prev.intermediate_tables || []),
        {
          table: '',
          intermediate_column: '',
          target_column: ''
        }
      ]
    }));
  };
  
  const updateIntermediateTable = (index: number, field: keyof IntermediateTableConfig, value: string) => {
    setConfig(prev => ({
      ...prev,
      intermediate_tables: prev.intermediate_tables?.map((table, i) => 
        i === index ? { ...table, [field]: value } : table
      ) || []
    }));
  };
  
  const removeIntermediateTable = (index: number) => {
    setConfig(prev => ({
      ...prev,
      intermediate_tables: prev.intermediate_tables?.filter((_, i) => i !== index) || []
    }));
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>
          <div>
            <h2 className="text-2xl font-bold">Custom Reference Field</h2>
            <p className="text-muted-foreground">
              Configure a custom reference field with advanced options
            </p>
          </div>
        </div>
        <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
          <Settings className="h-4 w-4 mr-2" />
          Save Configuration
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Database className="h-5 w-5" />
              <span>Basic Configuration</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Reference Table */}
            <div className="space-y-2">
              <Label htmlFor="ref_table">Reference Table *</Label>
              <SearchableSelect
                id="ref_table"
                value={config.ref_table}
                onValueChange={(value) => setConfig(prev => ({ ...prev, ref_table: value }))}
                options={tableOptions}
                placeholder="Select reference table"
              />
            </div>
            
            {/* Checklist Item chooser (only when referencing project_checklist_items) */}
            {isChecklistItemReference && (
              <div className="space-y-2">
                <Label htmlFor="checklist_item">Checklist Item to reference</Label>
                <SearchableSelect
                  id="checklist_item"
                  value={String(config.ref_filter_group?.value || '')}
                  onValueChange={(value) => setConfig(prev => ({
                    ...prev,
                    ref_filter_group: {
                      col_name: 'template_item_id',
                      operator: '=',
                      value: Number(value)
                    }
                  }))}
                  options={checklistItems.map(ci => ({
                    label: `${ci.template ? `[${ci.template}] ` : ''}${ci.name} (ID ${ci.id})`,
                    value: String(ci.id)
                  }))}
                  placeholder="Select checklist item to reference"
                />
                <p className="text-xs text-muted-foreground">This sets the template_item_id filter for the cross-checklist reference.</p>
              </div>
            )}

            {/* Reference Column */}
            <div className="space-y-2">
              <Label htmlFor="ref_col">Reference Column *</Label>
              <Input
                id="ref_col"
                value={config.ref_col}
                onChange={(e) => setConfig(prev => ({ ...prev, ref_col: e.target.value }))}
                placeholder="e.g., file_name, first_name, title"
              />
            </div>
            
            {/* Lookup Column */}
            <div className="space-y-2">
              <Label htmlFor="ref_lookup_col">Lookup Column</Label>
              <Input
                id="ref_lookup_col"
                value={config.ref_lookup_col || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, ref_lookup_col: e.target.value }))}
                placeholder="Default: id"
              />
              <p className="text-xs text-muted-foreground">
                Column used for lookup in the reference table. Defaults to "id" if left empty.
              </p>
            </div>
            
            {/* Field Type */}
            <div className="space-y-2">
              <Label htmlFor="field_type">Field Type</Label>
              <SearchableSelect
                id="field_type"
                value={config.field_type || 'TEXT'}
                onValueChange={(value) => setConfig(prev => ({ 
                  ...prev, 
                  field_type: value as RefConfig['field_type']
                }))}
                options={fieldTypeOptions}
                placeholder="Select field type"
              />
            </div>
            
            {/* Editable */}
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="is_editable" 
                checked={config.is_editable !== false}
                onCheckedChange={(checked) => setConfig(prev => ({
                  ...prev, 
                  is_editable: checked as boolean
                }))}
              />
              <Label htmlFor="is_editable">Field is editable</Label>
            </div>
          </CardContent>
        </Card>
        
        {/* Advanced Configuration */}
        <div className="space-y-6">
          {/* Filter Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Filter className="h-5 w-5" />
                <span>Filter Configuration</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show_filter" 
                  checked={showFilterConfig}
                  onCheckedChange={(checked) => setShowFilterConfig(checked as boolean)}
                />
                <Label htmlFor="show_filter">Apply filter to reference table</Label>
              </div>
              
              {showFilterConfig && (
                <div className="space-y-4 pl-6 border-l-2 border-gray-200">
                  <div className="space-y-2">
                    <Label htmlFor="filter_column">Filter Column</Label>
                    <Input
                      id="filter_column"
                      value={filterGroup.col_name}
                      onChange={(e) => setFilterGroup(prev => ({ ...prev, col_name: e.target.value }))}
                      placeholder="e.g., description, status, is_primary"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="filter_operator">Operator</Label>
                    <SearchableSelect
                      id="filter_operator"
                      value={filterGroup.operator}
                      onValueChange={(value) => setFilterGroup(prev => ({ 
                        ...prev, 
                        operator: value as RefFilterGroup['operator']
                      }))}
                      options={operatorOptions}
                      placeholder="Select operator"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="filter_value">Filter Value</Label>
                    <Input
                      id="filter_value"
                      value={filterGroup.value as string}
                      onChange={(e) => setFilterGroup(prev => ({ ...prev, value: e.target.value }))}
                      placeholder="e.g., New Proposal, pending, true"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Intermediate Tables */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Link className="h-5 w-5" />
                <span>Intermediate Tables</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show_intermediate" 
                  checked={showIntermediateTables}
                  onCheckedChange={(checked) => setShowIntermediateTables(checked as boolean)}
                />
                <Label htmlFor="show_intermediate">Use intermediate tables for lookup</Label>
              </div>
              
              {showIntermediateTables && (
                <div className="space-y-4">
                  {config.intermediate_tables?.map((table, index) => (
                    <div key={index} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Intermediate Table {index + 1}</h4>
                        <Button
                          onClick={() => removeIntermediateTable(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-3">
                        <div className="space-y-2">
                          <Label>Table Name</Label>
                          <Input
                            value={table.table}
                            onChange={(e) => updateIntermediateTable(index, 'table', e.target.value)}
                            placeholder="e.g., project_teams"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Intermediate Column</Label>
                          <Input
                            value={table.intermediate_column}
                            onChange={(e) => updateIntermediateTable(index, 'intermediate_column', e.target.value)}
                            placeholder="e.g., team_id"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Target Column</Label>
                          <Input
                            value={table.target_column}
                            onChange={(e) => updateIntermediateTable(index, 'target_column', e.target.value)}
                            placeholder="e.g., project_id"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <Button onClick={addIntermediateTable} variant="outline" className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Intermediate Table
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Configuration Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Configuration Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="text-sm overflow-x-auto">
              {JSON.stringify(config, null, 2)}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
